import { useQuery } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { trpc } from '@/lib/trpc'

/**
 * RFC-197 Part II, Phase D — the shared post-connect "Result" primitive: a
 * simple success confirmation for a freshly connected provider. The connect
 * flow only validates the connection; applicable checks materialize as CES in
 * the background (the integrations card shows that "Syncing checks…" status), so
 * this card does NOT track the worker reconcile — it just confirms success.
 * Shows an optional "N checks active" count when a program + provider are given.
 */
export function ConnectionResult({
  providerLabel,
  programId,
  provider,
  className,
}: {
  providerLabel: string
  /** When provided with `provider`, shows the "N checks active" count. */
  programId?: string
  provider?: string
  className?: string
}) {
  const enablement = useQuery({
    ...trpc.checker.previewEnablement.queryOptions({
      programId: programId ?? '',
      provider: provider ?? '',
    }),
    enabled: !!programId && !!provider,
  })
  const activeCount = enablement.data?.alreadyMaterializedTestCount ?? null

  return (
    <div
      className={`flex w-full max-w-sm items-center gap-3 rounded-sm border border-emerald-200/70 bg-emerald-50/50 px-3 py-2.5 ${className ?? ''}`}
    >
      <div className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-emerald-100'>
        <Check className='h-4 w-4 text-emerald-600' strokeWidth={2.5} />
      </div>
      <div className='min-w-0 flex-1'>
        <div className='text-[12px] font-medium leading-tight text-text-secondary'>
          {providerLabel}
        </div>
        <div className='text-[11px] leading-tight text-emerald-700'>
          {activeCount != null
            ? `Connected · ${activeCount} checks active`
            : 'Connected successfully'}
        </div>
      </div>
    </div>
  )
}
